import { useCallback, useEffect, useRef, useState, type CSSProperties, type ReactNode } from "react";
import styles from "./dropdownMenu.module.scss";

interface DropdownMenuProps {
  trigger: ReactNode;
  children: ReactNode;
  align?: "left" | "right";
}

export function DropdownMenu({ trigger, children, align = "right" }: DropdownMenuProps) {
  const [open, setOpen] = useState(false);
  const [menuStyle, setMenuStyle] = useState<CSSProperties>({});
  const rootRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const itemsRef = useRef<HTMLButtonElement[]>([]);
  const activeIndex = useRef(-1);

  const close = useCallback(() => {
    setOpen(false);
    activeIndex.current = -1;
  }, []);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    function onKeyDown(e: KeyboardEvent) {
      const target = e.target;
      const isTypingTarget =
        target instanceof HTMLInputElement
        || target instanceof HTMLTextAreaElement
        || (target instanceof HTMLElement && target.isContentEditable);
      const items = itemsRef.current.filter(Boolean);
      switch (e.key) {
        case "Escape":
          e.preventDefault();
          close();
          break;
        case "ArrowDown":
          if (isTypingTarget) return;
          e.preventDefault();
          activeIndex.current = Math.min(activeIndex.current + 1, items.length - 1);
          items[activeIndex.current]?.focus();
          break;
        case "ArrowUp":
          if (isTypingTarget) return;
          e.preventDefault();
          activeIndex.current = Math.max(activeIndex.current - 1, 0);
          items[activeIndex.current]?.focus();
          break;
      }
    }
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open, close]);

  // Close when clicking/tapping anywhere outside the dropdown root.
  useEffect(() => {
    if (!open) return;

    function onPointerDown(e: PointerEvent) {
      const root = rootRef.current;
      if (!root) return;
      if (e.target instanceof Node && !root.contains(e.target)) {
        close();
      }
    }

    document.addEventListener("pointerdown", onPointerDown, true);
    return () => document.removeEventListener("pointerdown", onPointerDown, true);
  }, [open, close]);

  useEffect(() => {
    if (!open) return;

    function positionMenu() {
      const root = rootRef.current;
      const menu = menuRef.current;
      if (!root || !menu) return;

      const triggerRect = root.getBoundingClientRect();
      const menuRect = menu.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const viewportPadding = 8;
      const gap = 4;

      const preferredTop = triggerRect.bottom + gap;
      const availableBelow = viewportHeight - preferredTop - viewportPadding;
      const availableAbove = triggerRect.top - gap - viewportPadding;
      const openUp = availableBelow < 220 && availableAbove > availableBelow;

      const maxHeight = Math.max(
        160,
        Math.min(420, openUp ? availableAbove : availableBelow),
      );

      const menuHeight = Math.min(menuRect.height || maxHeight, maxHeight);
      const unclampedTop = openUp
        ? triggerRect.top - gap - menuHeight
        : preferredTop;
      const top = Math.max(
        viewportPadding,
        Math.min(unclampedTop, viewportHeight - menuHeight - viewportPadding),
      );

      const menuWidth = menuRect.width || 180;
      const unclampedLeft = align === "right"
        ? triggerRect.right - menuWidth
        : triggerRect.left;
      const left = Math.max(
        viewportPadding,
        Math.min(unclampedLeft, viewportWidth - menuWidth - viewportPadding),
      );

      setMenuStyle({
        position: "fixed",
        top: `${Math.round(top)}px`,
        left: `${Math.round(left)}px`,
        maxHeight: `${Math.round(maxHeight)}px`,
      });
    }

    positionMenu();
    const raf = window.requestAnimationFrame(positionMenu);
    window.addEventListener("resize", positionMenu);
    window.addEventListener("scroll", positionMenu, true);

    return () => {
      window.cancelAnimationFrame(raf);
      window.removeEventListener("resize", positionMenu);
      window.removeEventListener("scroll", positionMenu, true);
    };
  }, [open, align]);

  return (
    <div ref={rootRef} style={{ position: "relative", display: "inline-block" }}>
      <div onClick={() => setOpen((v) => !v)}>{trigger}</div>
      {open && (
        <>
          <div
            ref={menuRef}
            className={styles.menu}
            role="menu"
            style={menuStyle}
          >
            <DropdownItemContext.Provider value={{
              close,
              registerItem: (el) => {
                if (!el || itemsRef.current.includes(el)) return;
                itemsRef.current.push(el);
              },
            }}>
              {children}
            </DropdownItemContext.Provider>
          </div>
        </>
      )}
    </div>
  );
}

import { createContext, useContext } from "react";

const DropdownItemContext = createContext<{
  close: () => void;
  registerItem: (el: HTMLButtonElement | null) => void;
}>({ close: () => {}, registerItem: () => {} });

interface DropdownMenuItemProps {
  onClick?: () => void;
  disabled?: boolean;
  children: ReactNode;
}

export function DropdownMenuItem({ onClick, disabled, children }: DropdownMenuItemProps) {
  const { close, registerItem } = useContext(DropdownItemContext);
  return (
    <button
      ref={registerItem}
      role="menuitem"
      className={styles.item}
      disabled={disabled}
      onClick={() => {
        onClick?.();
        close();
      }}
    >
      {children}
    </button>
  );
}

export function DropdownMenuSeparator() {
  return <div className={styles.separator} role="separator" />;
}
